import { inject, Injectable } from '@angular/core';
import {
  Category,
  mapProductDetailsDtoToInternal,
  PaginatedOffset,
  ProductDetails,
  ProductDetailsDto,
  ProductDetailsWithVariant,
  ProductLegacy,
  ProductSearchQueryParams,
  ProductVariance,
  RelatedProduct,
  VariantGrouping,
} from '@models';
import { removeObjectEmptyFields } from '@shared';
import { map, Observable, of, Subject, tap } from 'rxjs';
import { NavigationService } from '@core';
import { ApiService } from './api-service';

// Complete product response cache interface
interface CachedProductResponse {
  rawResponse: any; // The complete API response
  productDetails: ProductDetails; // Mapped product details
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly apiService = inject(ApiService);
  private navigationService = inject(NavigationService);

  // Unified product data cache with 10-minute timeout
  private productResponseCache = new Map<string, CachedProductResponse>();
  private readonly CACHE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

  private productDataLoadedSubject = new Subject<{ productId: string, productDetails: ProductDetails }>();
  public productDataLoaded$ = this.productDataLoadedSubject.asObservable();

  /**
   * Type guard to check if response is valid ProductDetailsDto
   */
  private isValidProductResponse(response: unknown): response is ProductDetailsDto {
    return response !== null && typeof response === 'object' && 'productId' in response;
  }
  /**
   * Cache utility methods
   */
  private isCacheValid(cachedItem: CachedProductResponse): boolean {
    return (Date.now() - cachedItem.timestamp) < this.CACHE_TIMEOUT_MS;
  }

  private getCachedProductResponse(productId: string): CachedProductResponse | null {
    const cached = this.productResponseCache.get(productId);
    if (cached && this.isCacheValid(cached)) {
      return cached;
    } else if (cached) {
      this.productResponseCache.delete(productId);
    }
    return null;
  }

  private setCachedProductResponse(productId: string, rawResponse: any, productDetails: ProductDetails): void {
    const cachedData: CachedProductResponse = {
      rawResponse,
      productDetails,
      timestamp: Date.now()
    };
    this.productResponseCache.set(productId, cachedData);
  }

  /**
   * Main product fetch method
   */
  public getProductByProdID(id: string): Observable<ProductDetails> {
  // Check cache first
  const cached = this.getCachedProductResponse(id);
  if (cached) {
    setTimeout(() => {
      this.productDataLoadedSubject.next({ productId: id, productDetails: cached.productDetails });
    }, 0);
    return of(cached.productDetails);
  }

  // Make single API call and cache both raw response and mapped data
  return (this.apiService.get(`products/${id}`) as Observable<ProductDetailsDto>).pipe(
    tap((rawResponse) => {
      // Cache the raw response immediately
      const productDetails = mapProductDetailsDtoToInternal(rawResponse);
      this.setCachedProductResponse(id, rawResponse, productDetails);
      this.productDataLoadedSubject.next({ productId: id, productDetails });
    }),
    map(mapProductDetailsDtoToInternal)
  );
}

  /**
   * Optimized method - gets product variances from cache or makes API call
   */
  public getProductVariances(id: string): Observable<ProductVariance[]> {
    const cached = this.getCachedProductResponse(id);
    if (cached) {
      return of(cached.rawResponse.productVariances ?? []);
    }
    return this.apiService.get(`products/${id}`).pipe(
      map((response: unknown) => {
        if (!this.isValidProductResponse(response)) {
          throw new Error(`Invalid product response for variances, ID: ${id}`);
        }
        
        // Cache the response for future use
        const productDetails = mapProductDetailsDtoToInternal(response);
        this.setCachedProductResponse(id, response, productDetails);
        
        return (response as any).productVariances ?? [];
      })
    );
  }

  /**
   * Optimized method - gets variant groupings from cache or makes API call
   */
  public getVariantGroupings(id: string): Observable<VariantGrouping[]> {
    const cached = this.getCachedProductResponse(id);
    if (cached) {
      return of(cached.rawResponse.variantGroupings ?? []);
    }
    return this.apiService.get(`products/${id}`).pipe(
      map((response: unknown) => {
        if (!this.isValidProductResponse(response)) {
          throw new Error(`Invalid product response for groupings, ID: ${id}`);
        }
        
        // Cache the response for future use
        const productDetails = mapProductDetailsDtoToInternal(response);
        this.setCachedProductResponse(id, response, productDetails);
        
        return (response as any).variantGroupings ?? [];
      })
    );
  }

  /**
   * gets related products from cache or makes API call
   */
  public getRelatedProducts(id: string): Observable<RelatedProduct[]> {
    const cached = this.getCachedProductResponse(id);
    if (cached) {
      return of(cached.rawResponse.relatedProducts ?? []);
    }
    return this.apiService.get(`products/${id}`).pipe(
      map((response: unknown) => {
        if (!this.isValidProductResponse(response)) {
          throw new Error(`Invalid product response for related products, ID: ${id}`);
        }
        
        // Cache the response for future use
        const productDetails = mapProductDetailsDtoToInternal(response);
        this.setCachedProductResponse(id, response, productDetails);
        
        return (response as any).relatedProducts ?? [];
      })
    );
  }

  /**
   * Enhanced method to get complete product data with all related info
   */
  public getCompleteProductData(id: string): Observable<{
    productDetails: ProductDetails;
    productVariances: ProductVariance[];
    variantGroupings: VariantGrouping[];
    relatedProducts: RelatedProduct[];
  }> {
    const cached = this.getCachedProductResponse(id);
    if (cached) {
       setTimeout(() => {
      this.productDataLoadedSubject.next({ productId: id, productDetails: cached.productDetails });
    }, 0);
      return of({
        productDetails: cached.productDetails,
        productVariances: cached.rawResponse.productVariances ?? [],
        variantGroupings: cached.rawResponse.variantGroupings ?? [],
        relatedProducts: cached.rawResponse.relatedProducts ?? []
      });
    }
    return this.apiService.get(`products/${id}`).pipe(
      map((response: unknown) => {
        if (!this.isValidProductResponse(response)) {
          throw new Error(`Invalid product response for complete data, ID: ${id}`);
        }
        
        const productDetails = mapProductDetailsDtoToInternal(response);
        
        // Cache the complete response
        this.setCachedProductResponse(id, response, productDetails);
        this.productDataLoadedSubject.next({ productId: id, productDetails });

        return {
          productDetails,
          productVariances: (response as any).productVariances ?? [],
          variantGroupings: (response as any).variantGroupings ?? [],
          relatedProducts: (response as any).relatedProducts ?? []
        };
      })
    );
  }
  // Existing methods that don't need caching (different endpoints)
  public goToProductDetails(p: ProductDetails | ProductLegacy | any): void {
    const isProductDetailsType = p.productId;
    const isProductType = p.ProductID;
    if (isProductDetailsType) {
      this.navigationService.goToProductPage(p.productId, p.brandId);
    } else if (isProductType) {
      this.navigationService.goToProductPage(p.ProductID, p.BrandId);
    }
  }

  search<T = ProductDetailsDto, Q = ProductSearchQueryParams, P = PaginatedOffset<T>>(
    params: Partial<Q>,
  ): Observable<P> {
    const filteredParams = removeObjectEmptyFields(params);
      return this.apiService.get<P>('search/find', filteredParams);
  }

  getProducts({
    categoryIds = [],
    brandIds,
    sortBy,
    offset,
    count,
    priceFrom,
    priceTo,
    isPromo,
  }: ProductSearchQueryParams): Observable<PaginatedOffset<ProductDetailsDto>> {
    const params = removeObjectEmptyFields({
      categoryIds,
      brandIds,
      sortBy,
      offset,
      count,
      priceFrom,
      priceTo,
      isPromo,
    });
    return this.apiService.get<PaginatedOffset<ProductDetailsDto>>('products', params);
  }

  getCategoriesForBreadcrumb(categoryId: string | number): Observable<any> {
    return this.apiService.get(`categories/${categoryId}/hierarchy`);
  }

  getCategoriesForAppleBreadcrumb(categoryId: string | number): Observable<any> {
    return this.apiService.get(`categories/${categoryId}/hierarchy-topdown`);
  }

  getProductByCategory(categoryId: string): Observable<any> {
    return this.apiService.get(`search/find?CategoryIds=${categoryId}`);
  }
    /**
   * Get product data from cache only (no API fallback)
   */
  public getCachedProductData(id: string): {
    productDetails: ProductDetails;
    productVariances: ProductVariance[];
    variantGroupings: VariantGrouping[];
    relatedProducts: RelatedProduct[];
  } | null {
    const cached = this.getCachedProductResponse(id);
    if (cached) {
      return {
        productDetails: cached.productDetails,
        productVariances: cached.rawResponse.productVariances ?? [],
        variantGroupings: cached.rawResponse.variantGroupings ?? [],
        relatedProducts: cached.rawResponse.relatedProducts ?? []
      };
    }
    return null;
  }
  clearExpiredProductCache(): void {
    const now = Date.now();
    let removedCount = 0;
    
    for (const [productId, cached] of this.productResponseCache.entries()) {
      if (!this.isCacheValid(cached)) {
        this.productResponseCache.delete(productId);
        removedCount++;
      }
    }
  }

  clearAllProductCache(): void {
    const count = this.productResponseCache.size;
    this.productResponseCache.clear();
  }

  getProductCacheStats(): {cacheSize: number, validEntries: number} {
    let validEntries = 0;
    
    for (const cached of this.productResponseCache.values()) {
      if (this.isCacheValid(cached)) {
        validEntries++;
      }
    }
    
    return {
      cacheSize: this.productResponseCache.size,
      validEntries
    };
  }
}